import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthLayout from "../../components/auth/AuthLayout";
import AuthForm from "../../components/auth/AuthForm";
import PasswordInput from "../../components/auth/PasswordInput";
import LoadingIndicator from "../../components/auth/LoadingIndicator";
import Button from "../../components/ui/button/Button";
import { supabase } from "../../integrations/supabase/client";

export default function UpdatePassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const confirmPasswordInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [initialAuthCheck, setInitialAuthCheck] = useState(false);

  useEffect(() => {
    // Check if user is coming from a password reset flow
    const checkAuth = async () => {
      try {
        //console.log("URL:", window.location.href);
        //console.log("Query params:", window.location.search);
        
        // Extract token parameters from URL if present
        const urlParams = new URLSearchParams(window.location.search);
        const type = urlParams.get('type');
        const token = urlParams.get('token');
        const redirectTo = urlParams.get('redirect_to');
        
        //console.log("URL parameters:", { type, token: token ? "exists" : "missing", redirectTo });
        
        // If we have a recovery token, proceed with password reset flow
        if (type === 'recovery' && token) {
          //console.log("Found recovery token, proceeding with password reset flow");
          // Even if we don't have a session yet, allow the user to attempt the password update
          // Supabase will handle the recovery token during the updateUser call
          setInitialAuthCheck(true);
          return;
        }
        
        // When redirected from Supabase after clicking password reset link,
        // Supabase automatically handles the token in the URL and creates a session
        const { data, error } = await supabase.auth.getUser();
        
        //console.log("User check:", data.user ? "User exists" : "No user");
        
        if (error) {
          console.error('Authentication error:', error);
          setErrorMessage('Authentication error. Please try requesting a new password reset link.');
          setTimeout(() => {
            navigate('/forgot-password');
          }, 3000);
          return;
        }
        
        if (!data.user) {
          // No valid session and no recovery token
          console.log('No session found. Redirecting to forgot-password.');
          setErrorMessage('Your password reset link is invalid or has expired. Please request a new one.');
          setTimeout(() => {
            navigate('/forgot-password');
          }, 3000);
        } else {
          // We have a valid session
          setInitialAuthCheck(true);
        }
      } catch (err) {
        console.error('Error checking auth state:', err);
        setErrorMessage('An error occurred. Please try again.');
      }
    };

    checkAuth();
  }, [navigate, location]);

  const checkPasswordsMatch = () => {
    const password = passwordInputRef.current?.value || '';
    const confirmPassword = confirmPasswordInputRef.current?.value || '';
    
    if (confirmPassword && password !== confirmPassword) {
      setPasswordsMatch(false);
    } else {
      setPasswordsMatch(true);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsLoading(true);
    
    const password = passwordInputRef.current?.value || '';
    const confirmPassword = confirmPasswordInputRef.current?.value || '';

    if (!password) {
      setErrorMessage('Please enter a new password');
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      setErrorMessage('Password must be at least 6 characters');
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      setPasswordsMatch(false);
      setIsLoading(false);
      return;
    }

    try {
      // Check if we have a recovery token in the URL
      const urlParams = new URLSearchParams(window.location.search);
      const isRecoveryFlow = urlParams.get('type') === 'recovery';
      
      console.log("Updating password, recovery flow:", isRecoveryFlow);
      
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        console.error("Update password error:", error);
        setErrorMessage(error.message);
      } else {
        // Show a more prominent success message
        setSuccessMessage('Your password has been updated successfully! You will be redirected to the sign-in page in a few seconds.');
        
        // Redirect after a delay
        setTimeout(() => {
          navigate('/signin', { 
            state: { 
              message: "Your password has been updated successfully! You can now sign in with your new password.",
              status: "success"
            } 
          });
        }, 4000);

        // Sign out the user to ensure they sign in with the new password
        await supabase.auth.signOut();
      }
    } catch (err) {
      console.error('Password update error:', err);
      setErrorMessage('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!initialAuthCheck) {
    return <LoadingIndicator message="Verifying your password reset request..." errorMessage={errorMessage} />;
  }

  return (
    <AuthLayout
      title="Update Password"
      subtitle="Create a new password for your account"
      backLink={{ to: "/signin", label: "Back to sign in" }}
      metaTitle="Update Password | PDF ConvertOne"
      metaDescription="Update your PDF ConvertOne account password"
    >
      <AuthForm
        onSubmit={handleUpdatePassword}
        errorMessage={errorMessage}
        successMessage={successMessage}
      >
        <PasswordInput
          label="New Password"
          placeholder="Enter your new password"
          ref={passwordInputRef}
          onChange={checkPasswordsMatch}
          minLength={6}
          required
        />

        <PasswordInput
          label="Confirm Password"
          placeholder="Confirm your new password"
          ref={confirmPasswordInputRef}
          onChange={checkPasswordsMatch}
          minLength={6}
          required
          className={!passwordsMatch ? "border-error-500 focus:border-error-500" : ""}
          errorMessage={!passwordsMatch ? "Passwords do not match" : undefined}
        />
        
        <div>
          <Button className="w-full" size="sm" disabled={isLoading || !passwordsMatch}>
            {isLoading ? 'Updating...' : 'Update Password'}
          </Button>
        </div>
      </AuthForm>
    </AuthLayout>
  );
} 